import Luminary from '../index';
import { roundToPrecision, snapTimeUpToNextFrameBoundary } from '../libs/frameUtils';
import { getGlobalAssetNetworkManager } from '../core/AssetNetworkManager';
import { Config } from '../video-data/config';
import { sliceVideoData } from '../video-data/sliceVideoData';
import { determineResolutionFromVideoData } from '../video-data/determineResolutionFromVideoData';

import { FETCH_ON_BUFFER, type MediaSize } from '../types';
import type { OpaqueVideoData } from '../video-data/types';
import { confirmWebGLSupport } from '../webGLSupported';
import { VideoDataSlice } from '../frameByFrameRenderer/VideoDataSlice';
import { ErrorReporter } from '../libs/error-reporter';
import type { Recorder, RecorderStats, FrameExtractor } from './types';

const FPS = 30;

export type RenderStats = {
    resolutionValue: number;
    duration: number;
    targetFPS: number;
    frameCount: number;
    videoDataLoadTime: number;
    fetchResourcesAndBuildHierarchyTime: number;
    imageCount: number;
    videoCount: number;
    lottieCount: number;
    textCount: number;
    fancyCaptionCount: number;
    renderTime: number;
    saveTime: number;
    totalTime: number;
    recorderStats: RecorderStats;
};

/**
 * Feature flags that we can use to enable/disable certain features within rendering.
 * The source of truth for these flags is in web/render_pipeline/tasks.py in queue_video_for_render
 */
export type RenderFlags = {} | null;

type FrameByFrameRendererSettings<FrameData = {}> = {
    startTime: number;
    endTime: number;
    fps?: number;
    mediaSize?: MediaSize;
    resolutionValue: number | null;
    videoData: OpaqueVideoData;
    recorder: Recorder<FrameData>;
    frameExtractor: FrameExtractor<FrameData>;
    flags?: RenderFlags;
    canvasParent?: HTMLElement;
};

/**
 * FrameByFrameRenderer does the following:
 * 1) loads video data into luminary
 * 2) waits for resources loading and building hierarchy
 * 4) starts recorder
 * 5) renders every frame, extracts Frame and pass it to recorder
 * 6) stops recorder and waits for it to finish
 *
 * It should not be platform specific, so it should not use any node/browser specific APIs.
 */
export class FrameByFrameRenderer {
    luminary: Luminary;
    #settings: FrameByFrameRendererSettings;

    constructor({
        mediaSize = Config.RENDER_MEDIA_SIZE,
        fps = FPS,
        ...settings
    }: FrameByFrameRendererSettings) {
        this.#settings = {
            ...settings,
            mediaSize,
            fps,
        };
        getGlobalAssetNetworkManager().MAX_SIMULTANEOUS_REQUESTS = 1;
    }
    async render(): Promise<RenderStats> {
        const { startTime, endTime, fps } = this.#settings;
        if (endTime < startTime) {
            throw Error(
                `Unable to render specified range, end time ${endTime} is smaller than ${startTime}`,
            );
        }

        if (!this.#settings.resolutionValue) {
            const resolutionValue = await determineResolutionFromVideoData(
                this.#settings.videoData,
            );
            console.log(`Using resolution of ${resolutionValue} from video data`);
            this.#settings.resolutionValue = resolutionValue;
        }

        const resolutionValue = this.#settings.resolutionValue;

        const performanceStartTime = Date.now();

        const durationMS = endTime - startTime;
        const duration = snapTimeUpToNextFrameBoundary(durationMS / 1000, {
            frameRate: fps,
            timePrecision: Config.VIDEO_TIME_PRECISION,
        });
        console.log(`duration: ${duration}s`);
        const frameCount = Math.round(duration * fps);
        console.log('frame count:', frameCount);

        // this resolves the moment all videos generated one image.
        console.group('loading video data');
        const {
            videoDataLoadTime,
            fetchResourcesAndBuildHierarchyTime,
            videoWidth,
            videoHeight,
            adjustedStartTime,
        } = await this.#prepareLuminaryForRender();
        console.groupEnd();
        const loadTime = Date.now();

        console.group('starting recorder');
        const recorder = this.#settings.recorder;
        const extractor = this.#settings.frameExtractor;

        recorder.start(videoWidth, videoHeight, frameCount);
        console.groupEnd();

        // render and capture every frame
        console.group('starting rendering');
        const ft = {
            seek: { sum: 0, max: 0 },
            render: { sum: 0, max: 0 },
            readPixels: { sum: 0, max: 0 },
        };
        for (let i = 0; i < frameCount; i++) {
            const frameTimeMS = (i / fps) * 1000;
            const time = roundToPrecision(
                adjustedStartTime + frameTimeMS,
                Config.VIDEO_TIME_PRECISION_MS,
            );
            console.group(`rendering frame ${i + 1}/${frameCount} @${time}ms`);

            const t0 = performance.now();
            await this.luminary._seekToFrameAt(time);
            const t1 = performance.now();

            this.luminary.render();
            const t2 = performance.now();

            const frame = await extractor.extractFrame(this.luminary);
            const t3 = performance.now();

            await recorder.capture(frame);

            const seek = t1 - t0;
            const render = t2 - t1;
            const readPixels = t3 - t2;
            ft.seek.sum += seek;
            ft.seek.max = Math.max(ft.seek.max, seek);
            ft.render.sum += render;
            ft.render.max = Math.max(ft.render.max, render);
            ft.readPixels.sum += readPixels;
            ft.readPixels.max = Math.max(ft.readPixels.max, readPixels);

            console.groupEnd();
        }
        console.groupEnd();

        const n = frameCount;
        console.log(
            `[frame-timing] avg: seek=${(ft.seek.sum / n).toFixed(1)}ms render=${(ft.render.sum / n).toFixed(1)}ms readPixels=${(ft.readPixels.sum / n).toFixed(1)}ms`,
        );
        console.log(
            `[frame-timing] max: seek=${ft.seek.max.toFixed(1)}ms render=${ft.render.max.toFixed(1)}ms readPixels=${ft.readPixels.max.toFixed(1)}ms`,
        );

        console.log('|- completed rendering');
        const renderTime = Date.now();

        // convert and save to output
        const recorderStats = await recorder.stop();
        const saveTime = Date.now();

        // collect info
        const videoCount = this.luminary.scenes
            .map((scene) => scene.getAssetsByType(Config.MEDIA_ASSET_TYPE))
            .flat()
            .filter((asset) => asset.isVideo === true).length;
        const imageCount = this.luminary.scenes
            .map((scene) => scene.getAssetsByType(Config.MEDIA_ASSET_TYPE))
            .flat()
            .filter((asset) => asset.isVideo === false).length;
        const lottieCount = this.luminary.scenes
            .map((scene) => scene.getAssetsByType(Config.LOTTIE_ASSET_TYPE))
            .flat().length;
        const textCount = this.luminary.scenes
            .map((scene) => scene.getAssetsByType(Config.TEXT_ASSET_TYPE))
            .flat().length;
        const fancyCaptionCount = this.luminary.scenes
            .map((scene) => scene.getAssetsByType(Config.FANCY_CAPTION_ASSET_TYPE))
            .flat().length;

        // cleanup
        await this.destroy();

        const renderStats: RenderStats = {
            resolutionValue,
            frameCount,
            duration,
            targetFPS: fps,
            videoDataLoadTime,
            fetchResourcesAndBuildHierarchyTime,
            renderTime: renderTime - loadTime,
            saveTime: saveTime - renderTime,
            totalTime: saveTime - performanceStartTime,
            imageCount,
            videoCount,
            lottieCount,
            textCount,
            fancyCaptionCount,
            recorderStats,
        };

        this.#logRenderStats(renderStats);

        return renderStats;
    }

    #logRenderStats(renderStats: RenderStats) {
        const {
            frameCount,
            videoDataLoadTime,
            fetchResourcesAndBuildHierarchyTime,
            imageCount,
            videoCount,
            lottieCount,
            textCount,
            fancyCaptionCount,
            renderTime,
            saveTime,
            totalTime,
            recorderStats,
        } = renderStats;

        console.log('-----------------------------');
        console.group('slice summary:');
        console.log(`frame count: ${frameCount}`);
        console.log(`video data loading time: ${videoDataLoadTime}ms`);
        console.log(
            `fetching resources and building hierarchy time: ${fetchResourcesAndBuildHierarchyTime}ms`,
        );
        console.log('asset count', {
            images: imageCount,
            videos: videoCount,
            lottie: lottieCount,
            text: textCount,
            'fancy captions': fancyCaptionCount,
        });
        console.log(`render time: ${renderTime}ms`);
        console.log(`render fps: ${1000 / (renderTime / frameCount)}fps`);
        console.log(`save time: ${saveTime}ms`);
        console.log(`total time: ${totalTime}ms`);
        console.log(`recorderStats`, recorderStats);
        console.groupEnd();
    }

    async destroy() {
        await this.luminary.destroy();
    }

    /**
     * Loads video data into luminary, waits for resources loading and building hierarchy
     */
    async #prepareLuminaryForRender(): Promise<{
        videoDataLoadTime: number;
        fetchResourcesAndBuildHierarchyTime: number;
        videoWidth: number;
        videoHeight: number;
        adjustedStartTime: number;
    }> {
        await confirmWebGLSupport();

        const { fps, resolutionValue, mediaSize, flags, canvasParent } = this.#settings;

        if (flags) {
            console.log(`rendering with flags: ${JSON.stringify(flags)}`);
        }
        const luminary = new Luminary({
            fetchResources: {
                transitions: true,
                media: FETCH_ON_BUFFER,
                fonts: true,
                audio: true,
            },
            resolutionValue,
            mediaSizeToUse: mediaSize,
            targetFPS: fps,
            loadStatusMaxCount: 3,
            seekWaitTime: 45000,
            antialias: false,
            // Download one resource at the time
            // NOTE: previously this was set to 50, but that caused connection errors. It seems that
            // Image assets can not download the same source multiple times at the same time.
            maxSimultaneousRequests: 1,
        });
        this.luminary = luminary;
        if (canvasParent) {
            luminary.canvasParent = canvasParent;
        }
        const performancestartTime = Date.now();

        let { slideRange, startTime, endTime, videoData } = await VideoDataSlice.slice(
            this.#settings.videoData,
            this.#settings.startTime,
            this.#settings.endTime,
            fps,
        );
        this.#settings.startTime = startTime;
        this.#settings.endTime = endTime;
        this.#settings.videoData = videoData;
        const { videoData: updateVideoData, prependedScene } = sliceVideoData(videoData, {
            start: slideRange.start,
            end: slideRange.end,
            preserveTransitions: true,
        });
        luminary.load(updateVideoData);

        if (prependedScene) {
            if (luminary.scenes.length <= 1) {
                ErrorReporter.setContext('renderer', { slideRange });
                throw new Error('Slicing video data resulted in removing all scenes');
            }
            // adjust time to account for the prepended scene
            const timeShift = luminary.scenes[1].startTime;
            startTime += timeShift;
        }

        const videoDataLoadingEndTime = Date.now();
        const { width, height } = luminary.outputResolution;

        console.log('|- loaded video data');
        console.log('|- ', luminary.getTimeSlices());
        await luminary.onAssetsLoaded();
        console.log('|- processed all changes');

        const performanceEndTime = Date.now();

        return {
            videoDataLoadTime: videoDataLoadingEndTime - performancestartTime,
            fetchResourcesAndBuildHierarchyTime: performanceEndTime - videoDataLoadingEndTime,
            videoWidth: width,
            videoHeight: height,
            adjustedStartTime: startTime,
        };
    }
}
